// src/AuthProvider.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Hub } from "aws-amplify/utils";
import {
  getCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
} from "aws-amplify/auth";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const INACTIVITY_MS = 15 * 60 * 1000; // 15 min
const TOKEN_SKEW_MS = 30 * 1000;      // 30s buffer
const DEVELOPER_GROUP = process.env.REACT_APP_DEVELOPER_GROUP || "Developers";

function getDeveloperAllowlist() {
  return new Set(
    String(process.env.REACT_APP_DEVELOPER_USERNAMES || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [autoLogoutSuppressed, setAutoLogoutSuppressed] = useState(false);

  const inactivityTimer = useRef(null);
  const tokenTimer = useRef(null);

  // ------------- helpers -------------
  const clearTimers = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (tokenTimer.current) clearTimeout(tokenTimer.current);
    inactivityTimer.current = null;
    tokenTimer.current = null;
  };

  const broadcastLogout = (reason) => {
    // Notify other tabs. (No read-back in the same tab.)
    localStorage.setItem("snmd:logout", JSON.stringify({ at: Date.now(), reason }));
  };

  // Clears state/timers ONLY. Use when sign-out originated elsewhere (Hub/storage).
  const localSignOut = (reason = "external") => {
    clearTimers();
    setAutoLogoutSuppressed(false);
    setUser(null);
  };

  // Full sign-out that also calls Amplify (we initiated it).
  const hardSignOut = async (reason = "manual") => {
    clearTimers();
    setAutoLogoutSuppressed(false);
    setUser(null);
    broadcastLogout(reason); // tell other tabs
    try {
      await amplifySignOut();
    } catch {
      /* ignore */
    }
    // If you want a redirect, do it in your router (App.js) based on user === null
  };

  const startInactivityTimer = () => {
    if (!user) return;
    if (autoLogoutSuppressed) return;
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => hardSignOut("inactivity"), INACTIVITY_MS);
  };

  const onUserActivity = () => {
    if (document.visibilityState !== "visible") return;
    if (!user) return;
    if (autoLogoutSuppressed) return;
    startInactivityTimer();
  };

  const scheduleTokenExpiryCheck = async () => {
    try {
      const session = await fetchAuthSession(); // refreshes if possible
      const accessExp = session.tokens?.accessToken?.payload?.exp;
      const idExp = session.tokens?.idToken?.payload?.exp;
      const expSec = accessExp ?? idExp;
      if (!expSec) return;

      const expMs = expSec * 1000;
      const wait = Math.max(0, expMs - Date.now() - TOKEN_SKEW_MS);

      clearTimeout(tokenTimer.current);
      tokenTimer.current = setTimeout(() => hardSignOut("token_expired"), wait);
    } catch {
      // Session fetch failed — treat as signed out locally
      localSignOut("session_error");
    }
  };

  const loginFromAmplify = async () => {
    try {
      const cur = await getCurrentUser(); // { username, userId, signInDetails }
      const session = await fetchAuthSession();
      const tokenGroups = session?.tokens?.idToken?.payload?.["cognito:groups"];
      const groups = Array.isArray(tokenGroups) ? tokenGroups : [];
      const allowlist = getDeveloperAllowlist();
      const username = cur?.username ?? cur?.userId ?? "unknown";
      const isDeveloper = groups.includes(DEVELOPER_GROUP) || allowlist.has(username);

      setUser({
        username,
        userId: cur?.userId,
        signInDetails: cur?.signInDetails,
        groups,
        isDeveloper,
      });
      startInactivityTimer();
      await scheduleTokenExpiryCheck();
    } catch {
      setUser(null);
      clearTimers();
    }
  };

  const login = async () => {
    await loginFromAmplify();
  };

  const logout = () => hardSignOut("manual");

  // ------------- effects -------------
  // Initial auth check
  useEffect(() => {
    loginFromAmplify();
  }, []);

  // Activity + cross-tab listeners
  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, onUserActivity, { passive: true }));
    document.addEventListener("visibilitychange", onUserActivity);

    const onStorage = (e) => {
      if (e.key === "snmd:logout") {
        // Another tab initiated logout — clear locally WITHOUT rebroadcasting or calling Amplify.
        localSignOut("storage_event");
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      clearTimers();
      events.forEach((e) => window.removeEventListener(e, onUserActivity));
      document.removeEventListener("visibilitychange", onUserActivity);
      window.removeEventListener("storage", onStorage);
    };
  }, [user, autoLogoutSuppressed]);

  // Amplify Hub auth lifecycle
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "signIn": // compat
          await loginFromAmplify();
          break;
        case "tokenRefresh":
          await scheduleTokenExpiryCheck();
          startInactivityTimer();
          break;
        case "tokenRefresh_failure":
        case "signIn_failure":
          // We initiate a real sign-out so Amplify clears everything
          await hardSignOut(payload.event);
          break;
        case "signedOut":
        case "signOut":
          // Already signed out by Amplify — just clear locally. DO NOT call amplifySignOut again.
          localSignOut("hub_signedOut");
          break;
        default:
          break;
      }
    });
    return () => unsubscribe();
  }, []);

  // Restart inactivity when suppression turns off
  useEffect(() => {
    if (!user) return;
    if (!autoLogoutSuppressed) startInactivityTimer();
    else clearTimeout(inactivityTimer.current);
  }, [autoLogoutSuppressed, user]);

  const value = {
    user,
    login,                   // call from LoginView on success
    logout,                  // manual sign-out button
    setAutoLogoutSuppressed, // true while recording/session active; false when done
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
